/**
 * External dependencies
 */
import { useEffect, useCallback, useRef } from 'react';
import useDidMount from '@naruto/naruto-hooks/src/useDidMount';
import useResize from '@naruto/naruto-hooks/src/useResize';
import useAutoLogout, { DEFAULT_PARAMS } from '@naruto/naruto-hooks/src/useAutoLogout';

/**
 * Internal dependencies
 */
import useEnv from '@naruto/bgdcms/src/hooks/useEnv';
import useMessage from '@naruto/bgdcms/src/hooks/useMessage';
import useReduxApi from '@naruto/bgdcms/src/hooks/useReduxApi';
import useMappedState from '@naruto/bgdcms/src/hooks/useMappedState';
import { MessageDialogList, MessageConfirmList, MessageSnackList, UseResize } from './types';

interface UseData {
  /* Data : General */
  userAuthCheck: boolean;
  messageDialogList: MessageDialogList;
  messageConfirmList: MessageConfirmList;
  messageSnackList: MessageSnackList;
  resize: UseResize;
  /* Refs */
  containerRef: React.RefObject<HTMLDivElement>;
  targetRef: React.RefObject<HTMLDivElement>;
  /* Functions : General */
  removeDialog: () => void;
  removeConfirm: () => void;
  removeSnack: () => void;
}

export const DEFAULT_RESIZE = 1200;

function useData(): UseData {
  /* Global & Local State */
  const env = useEnv();
  const storeUser = useMappedState(state => state.user);
  const storeMessage = useMappedState(state => state.message);
  const reduxUser = useReduxApi('user');
  const reduxMessage = useMessage();

  /* Refs */
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  /* Detect */
  const scale = useResize(containerRef, targetRef, DEFAULT_RESIZE, storeUser.userTokenInfo);

  /* Functions */
  const onLogout = useCallback((checkAndExpired?: boolean): void => {
    reduxUser('postUserLogout', [checkAndExpired]);
  }, []);
  /* Functions : Initialize */
  const initializeList = useCallback(() => {
    reduxUser('getUserIsLogin', []);
  }, []);
  const initialize = useCallback((): void => {
    initializeList();
  }, []);

  /* Hooks */
  useDidMount(() => {
    initialize();
  });
  useEffect(() => {
    if (storeUser.userSignOutOAuthUrl.link) {
      window.location.assign(storeUser.userSignOutOAuthUrl.link);
    }
  }, [storeUser.userSignOutOAuthUrl]);
  useAutoLogout(env.config.EnableAutoLogout, storeUser.userIsLogin, {
    ...DEFAULT_PARAMS,
    logoutFunction: onLogout,
    timeoutInSeconds: env.config.AutoLogoutTime,
    autoSignOutName: env.config.AutoLogoutLocalName
  });

  /* Main */
  return {
    /* Data : General */
    userAuthCheck: storeUser.userAuthCheck,
    messageDialogList: storeMessage.messageDialogList,
    messageConfirmList: storeMessage.messageConfirmList,
    messageSnackList: storeMessage.messageSnackList,
    resize: scale,
    /* Refs */
    containerRef,
    targetRef,
    /* Functions : General */
    removeDialog: reduxMessage.removeDialog,
    removeConfirm: reduxMessage.removeConfirm,
    removeSnack: reduxMessage.removeSnack
  };
}

export default useData;
